

































































































import Vue from 'vue';
import { mapState, mapGetters } from 'vuex';
import { AxiosError, AxiosResponse } from 'axios';

import CharacterAPI from '@/services/CharacterAPI';

import Util from '@/util/Util';

import Character from '@/types/Character';
import ImageInfo from '@/types/ImageInfo';

import ImageDisplayer from '@/components/ImageDisplayer.vue';
import LanguageSelectorTab from '@/components/LanguageSelectorTab.vue';
import Loader from '@/components/Loader.vue';
import ImageUploader from '@/components/ImageUploader.vue';

export default Vue.extend({
    name: 'CharacterEditor',

    components: {
        ImageDisplayer,
        LanguageSelectorTab,
        Loader,
        ImageUploader
    },

    props: {
        characterId: {
            type: String,
            default: ''
        }
    },

    data() {
        return {
            characters: new Array<Character>(),
            character: new Character(),
            error: {
                save: ''
            },
            status: '',
            progressImages: 0,
            progressIconImage: 0,
            successfullUpload: false,
            isLoadingCharacter: false,
            isLoadingCharacters: false,
            isSavingCharacter: false,
            selectedCharacterId: ''
        };
    },

    computed: {
        ...mapState(['language']),

        ...mapGetters(['characterById'])
    },

    created() {
        this.loadCharacters();
        if (Util.isObjectId(this.characterId)) {
            this.loadCharacter(this.characterId);
        } else {
            this.createNewCharacter();
        }
    },

    watch: {
        progressImages(val) {
            const vm = this;
            if (val === 100) {
                setTimeout(() => {
                    vm.progressImages = 0;
                    vm.successfullUpload = false;
                }, 5000);
            }
        },

        progressIconImage(val) {
            const vm = this;
            if (val === 100) {
                setTimeout(() => {
                    vm.progressIconImage = 0;
                    vm.successfullUpload = false;
                }, 5000);
            }
        }
    },

    methods: {
        move(from: number, to: number) {
            this.character.images.splice(to, 0, this.character.images.splice(from, 1)[0]);
        },

        createNewCharacter(): void {
            this.character = new Character();
        },

        saveIconImage(formData: FormData): void {
            const vm = this;
            const config = {
                onUploadProgress(progressEvent: ProgressEvent) {
                    vm.progressIconImage = progressEvent.loaded / progressEvent.total * 100;
                }
            };

            CharacterAPI
                .postIconImage(this.character.characterId, formData, config)
                .then((response: AxiosResponse) => {
                    this.successfullUpload = true;
                    this.character = response.data;
                })
                .catch((error: AxiosError) => {
                    if (error.response) {
                        if (error.response.status === 413) {
                            this.error.save = 'Image was to big to upload';
                        }
                        this.error.save = error.response.data.message;
                    }
                });
        },

        deleteIconImage(): void {
            CharacterAPI
                .deleteIconImage(this.character.characterId)
                .then((response: AxiosResponse) => {
                    this.successfullUpload = true;
                    this.character = response.data;
                })
                .catch((error: AxiosError) => {
                    if (error.response) {
                        if (error.response.status === 413) {
                            this.error.save = 'Image was to big to upload';
                        }
                        this.error.save = error.response.data.message;
                    }
                });
        },

        saveCharacter(): void {
            this.isSavingCharacter = true;
            const handleResponse = (response: AxiosResponse) => {
                    this.status = 'Saved successfully!';
                    this.createNewCharacter();
                    this.loadCharacters();
                };

            const handleError = (error: AxiosError) => {
                    this.error.save = 'Unable to load character';
                };

            CharacterAPI
                .post(this.character)
                .then(handleResponse)
                .catch((error: AxiosError) => this.error.save = 'Unable to save character')
                .finally(() => this.isSavingCharacter = false);
        },

        editCharcter(characterId: string): void {
            this.selectedCharacterId = characterId;
            this.loadCharacter(characterId);
            this.resetMessages();
            Util.scrollToElementWithId('character-editor');
        },

        resetMessages(): void {
            this.status = '';
            this.error.save = '';
        },

        loadCharacter(characterId: string): void {
            this.isLoadingCharacter = true;
            const handleResponse = (response: AxiosResponse) => {
                    this.character = response.data;
                };

            const handleError = (error: AxiosError) => {
                    this.error.save = 'Unable to load character';
                };

            CharacterAPI
                .getCharacter(characterId)
                .then(handleResponse)
                .catch(handleError)
                .finally(() => this.isLoadingCharacter = false);
        },

        loadCharacters(): void {
            this.isLoadingCharacters = true;
            CharacterAPI
                .getAll()
                .then((axiosResponse: AxiosResponse) => this.characters = axiosResponse.data)
                .catch((error: AxiosError) => this.error.save = 'Unable to load characters')
                .finally(() => this.isLoadingCharacters = false);
        },

        saveImage(formData: FormData): void {
            const vm = this;
            const config = {
                onUploadProgress(progressEvent: ProgressEvent) {
                    vm.progress = progressEvent.loaded / progressEvent.total * 100;
                }
            };

            const handleResponse = (response: AxiosResponse) => {
                    this.successfullUpload = true;
                    this.character = response.data;
                };

            const handleError = (error: AxiosError) => {
                    if (error.response) {
                        if (error.response.status === 413) {
                            this.error.save = 'Image was to big to upload';
                        }
                        this.error.save = error.response.data.message;
                    }
                };

            CharacterAPI
                .postImage(this.character.characterId, formData, config)
                .then(handleResponse)
                .catch(handleError);
        },

        deleteImage(image: ImageInfo): void {
            CharacterAPI
                .deleteImage(this.character.characterId, image.imageId)
                .then((response: AxiosResponse) => {
                    this.character = response.data;
                })
                .catch((error: AxiosError) => {
                    if (error.response) {
                        this.error.save = error.response.data.message;
                    }
                });
        },

        characterCheck(character: Character): string {
            const errors = new Array<string>();
            if (!character.iconImage) {
                errors.push('Icon Image');
            }
            if (!character.images || character.images.length === 0) {
                errors.push('Images');
            }
            return errors.join(', ');
        }
    }
});
